/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React from 'react';
import moment from 'moment';
import { Text, Counter } from '@usealloy/component-library';
import { formatCurrencyIntl } from '~/helpers/i18n';
import { components } from 'react-select';
import { TransactionStatusBadge } from './TransactionStatusBadge';

export const UNKNOWN_VALUE = '—';

export const FACETS = [
  {
    key: 'date', // t.created_at is aliased
    label: 'Date Range',
    filter_type: 'timestamp',
  },
  {
    key: 'flow_amount',
    label: 'Flow',
    rule_type: 'function',
    dropdown_label: {
      value: 'all',
      label: 'All',
      booleanFilter(value) {
        return typeof value === 'number';
      },
    },
    options: [
      {
        value: 'credit',
        label: 'Credit',
        booleanFilter(value) {
          return value >= 0;
        },
      },
      {
        value: 'debit',
        label: 'Debit',
        booleanFilter(value) {
          return value < 0;
        },
      },
    ],
  },
];

export const HIDDEN_FACETS = [
  {
    key: 'process_method',
    label: 'Process Method',
    rule_type: 'function',
    dropdown_label: {
      value: 'all',
      label: 'All',
      booleanFilter(value) {
        return typeof value === 'string';
      },
    },
    options: [],
  },
  {
    key: 'amount',
    label: 'Amount',
    rule_type: 'function',
    dropdown_label: {
      value: 'all',
      label: 'All',
      booleanFilter(value) {
        return typeof value === 'number';
      },
    },
    options: [
      // these are set as the amount option is selected! "amounts" are dependent on currency
      // and they are kind of constants - but dependent on a primary_currency_code
      // and (sometimes) the user input
    ],
  },
  {
    key: 'category',
    label: 'Category',
    rule_type: 'function',
    dropdown_label: {
      value: 'all',
      label: 'All',
      booleanFilter(value) {
        return typeof value === 'string';
      },
    },
    options: [],
  },
  {
    key: 'product_name',
    label: 'Product Name',
    rule_type: 'function',
    dropdown_label: {
      value: 'all',
      label: 'All',
      booleanFilter(value) {
        return typeof value === 'string';
      },
    },
    options: [],
  },
  {
    key: 'interaction_point',
    label: 'Interaction Point',
    rule_type: 'function',
    dropdown_label: {
      value: 'all',
      label: 'All',
      booleanFilter(value) {
        return typeof value === 'string';
      },
    },
    options: [],
  },
];

export const DEFAULT_COLUMNS = {
  priority: [
    'Transaction ID',
    'date',
    'account_number',
    'product_name',
    'amount',
    'status',
    'transaction_type',
    'transaction_description',
    'MCC',
    'MCC Description',
    'evidence_count',
    'currency_code',
    'transaction_code',
    'process_method',
    'category',
    'Counter-Party Country Code',
    'Counter-Party Name',
    'Counter-Party Sanctions',
    'settled_at',
    'is_approved',
    'interaction_point',
    'is_reversal',
    'card_process_mode',
    'is_international_override',
    'metadata',
  ],
  secondary: [
    'device_type_code',
    'approval_code',
    'batch_identifier',
    'is_triggered_by_user',
    'outcome',
  ],
  /*
    Hidden columns: still optional to select for CSV download.
    But should be hidden from table when selected, since there's
    a separate modal/customized view
  */
  hiddenFromTable: [
    'metadata',
  ],
  hiddenFromCSV: [
    'outcome_fa',
    'transaction_id',
    'evaluation_id',
    'entity_id',
    'flow_amount',
  ],
};

export const TRX_COLUMNS = [
  {
    maxSize: 110,
    header: 'Date',
    id: 'created_at',
    enableSorting: true,
    accessorFn: ({ created_at }) => (
      created_at && moment(created_at).isValid()
        ? moment(created_at).format('MM/DD/YYYY')
        : UNKNOWN_VALUE
    ),
  },
  {
    maxSize: 150,
    header: 'Amount',
    id: 'amount',
    enableSorting: true,
    accessorFn: ({ amount }) => amount,
    cell: ({ row: { original: { amount, currency_code: alphabeticCurrencyCode } } }) => (
      <Text style={{ color: amount < 0 ? 'var(--cl-red-500)' : '' }}>{formatCurrencyIntl(amount, { alphabeticCurrencyCode }).formattedNumber}</Text>
    ),
  },
  {
    maxSize: 125,
    header: 'Status',
    id: 'status',
    enableSorting: true,
    accessorFn: ({ status }) => status || UNKNOWN_VALUE,
    cell: ({ row: { original } }) => (
      original?.status
        ? <TransactionStatusBadge status={original.status} />
        : UNKNOWN_VALUE
    ),
  },
  {
    maxSize: 150,
    header: 'Transaction Type',
    id: 'type_description',
    enableSorting: true,
    accessorFn: ({ type_description }) => type_description || UNKNOWN_VALUE,
  },
  {
    maxSize: 200,
    header: 'Transaction Description',
    id: 'description',
    enableSorting: true,
    accessorFn: ({ description }) => description || UNKNOWN_VALUE,
  },
  {
    header: 'Transaction ID',
    id: 'external_transaction_identifier',
    enableSorting: true,
    accessorFn: ({ external_transaction_identifier }) => (
      external_transaction_identifier || UNKNOWN_VALUE
    ),
  },
  {
    header: 'Account Number',
    id: 'source_bank_account_number',
    enableSorting: true,
    accessorFn: ({ source_bank_account_number }) => source_bank_account_number || UNKNOWN_VALUE,
  },
  {
    header: 'Product Name',
    id: 'source_bank_product_name',
    enableSorting: true,
    accessorFn: ({ source_bank_product_name }) => source_bank_product_name || UNKNOWN_VALUE,
  },
  {
    header: 'MCC',
    id: 'target_bank_merchant_category_code',
    enableSorting: true,
    accessorFn: ({ target_bank_merchant_category_code }) => (
      target_bank_merchant_category_code || UNKNOWN_VALUE
    ),
  },
  {
    header: 'MCC Description',
    id: 'target_bank_mcc_description',
    enableSorting: true,
    accessorFn: ({ target_bank_mcc_description }) => target_bank_mcc_description || UNKNOWN_VALUE,
  },
  {
    maxSize: 100,
    header: 'Evidence',
    id: 'evidence_count',
    enableSorting: true,
    accessorFn: ({ evidence_count }) => evidence_count || 0,
    cell: ({ row: { original: { evidence_count } } }) => (
      evidence_count && evidence_count > 0
        ? <Counter count={evidence_count} />
        : ''
    ),
  },
  {
    header: 'Currency Code',
    id: 'currency_code',
    enableSorting: true,
    accessorFn: ({ currency_code }) => currency_code || UNKNOWN_VALUE,
  },
  {
    header: 'Transaction Code',
    id: 'type_code',
    enableSorting: true,
    accessorFn: ({ type_code }) => type_code || UNKNOWN_VALUE,
  },
  {
    header: 'Process Method',
    id: 'process_method',
    enableSorting: true,
    accessorFn: ({ process_method }) => process_method || UNKNOWN_VALUE,
  },
  {
    header: 'Category',
    id: 'category',
    enableSorting: true,
    accessorFn: ({ category }) => category || UNKNOWN_VALUE,
  },
  {
    header: 'Counter-Party Country Code',
    id: 'target_bank_country_code',
    enableSorting: true,
    accessorFn: ({ target_bank_country_code }) => target_bank_country_code || UNKNOWN_VALUE,
  },
  {
    header: 'Counter-Party Name',
    id: 'target_bank_account_name',
    enableSorting: true,
    accessorFn: ({ target_bank_account_name }) => target_bank_account_name || UNKNOWN_VALUE,
  },
  {
    header: 'Counter-Party Sanctions',
    id: 'matches',
    enableSorting: true,
    accessorFn: ({ matches }) => (typeof matches === 'number' && matches >= 0 ? matches : UNKNOWN_VALUE),
  },
  {
    header: 'Settled At',
    id: 'settled_at',
    enableSorting: true,
    accessorFn: ({ settled_at }) => settled_at || UNKNOWN_VALUE,
  },
  {
    header: 'Is Approved',
    id: 'is_approved',
    enableSorting: true,
    accessorFn: ({ is_approved }) => (
      typeof is_approved === 'boolean' ? is_approved : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Interaction Point',
    id: 'interaction_point',
    enableSorting: true,
    accessorFn: ({ interaction_point }) => interaction_point || UNKNOWN_VALUE,
  },
  {
    header: 'Is Reversal',
    id: 'is_reversal',
    enableSorting: true,
    accessorFn: ({ is_reversal }) => (
      typeof is_reversal === 'boolean' ? is_reversal : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Card Process Mode',
    id: 'card_process_mode',
    enableSorting: true,
    accessorFn: ({ card_process_mode }) => card_process_mode || UNKNOWN_VALUE,
  },
  {
    header: 'Is International Override',
    id: 'is_international_override',
    enableSorting: true,
    accessorFn: ({ is_international_override }) => (
      typeof is_international_override === 'boolean' ? is_international_override : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Device Type Code',
    id: 'device_type_code',
    enableSorting: true,
    accessorFn: ({ device_type_code }) => device_type_code || UNKNOWN_VALUE,
  },
  {
    header: 'Approval Code',
    id: 'approval_code',
    enableSorting: true,
    accessorFn: ({ approval_code }) => approval_code || UNKNOWN_VALUE,
  },
  {
    header: 'Batch Identifier',
    id: 'batch_identifier',
    enableSorting: true,
    accessorFn: ({ batch_identifier }) => batch_identifier || UNKNOWN_VALUE,
  },
  {
    header: 'Is Triggered By User',
    id: 'is_triggered_by_user',
    enableSorting: true,
    accessorFn: ({ is_triggered_by_user }) => (
      typeof is_triggered_by_user === 'boolean' ? is_triggered_by_user : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Outcome',
    id: 'outcome',
    enableSorting: true,
    accessorFn: ({ outcome }) => outcome || UNKNOWN_VALUE,
  },
];

export const TrxStatusOption = ({ children, ...restProps }) => (
  <components.Option className="d-flex align-items-center" {...restProps}>
    <TransactionStatusBadge status={children} />
  </components.Option>
);

export const TrxStatusSingleOption = ({ children, ...restProps }) => (
  <components.SingleValue className="d-flex align-items-center" {...restProps}>
    <TransactionStatusBadge status={children} />
  </components.SingleValue>
);

export const trxStatusOptions = [
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Rejected', label: 'Rejected' },
];
